// src/hooks/useAiChat.ts

import React from 'react';
import {
  useFetchChatSessionMessages,
  useFetchDriftPipeline,
  useLocalChatSessions,
} from './ai-hooks';
import { ChatMessage, ID, McpToolTrace } from '../types/aiTypes';
import { useChatStream } from './useChatStream';

function newSessionId(): string {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }

  return `sess_${Date.now()}_${Math.random().toString(16).slice(2)}`;
}

function resolvePipelineId(resp: any): string {
  const pipelineObj = Array.isArray(resp?.data) ? resp.data[0] : resp?.data;

  return (
    pipelineObj?._id ||
    pipelineObj?.id ||
    pipelineObj?.pipeline_id ||
    resp?.pipeline_id ||
    resp?.id ||
    ''
  );
}

type UseAiChatOptions = {
  initialSessionId?: string | null;
  defaultUseDrift?: boolean;
  defaultUseMcp?: boolean;
  related_type?: string;
  related_id?: ID;
};

export function useAiChat(options: UseAiChatOptions = {}) {
  const { sessions, upsertSession, deleteSession } = useLocalChatSessions();

  const [selectedSessionId, setSelectedSessionIdState] =
    React.useState<string | null>(
      options.initialSessionId || sessions?.[0]?.session_id || null
    );

  const [input, setInput] = React.useState('');
  const [messages, setMessages] = React.useState<ChatMessage[]>([]);
  const [toolTraces, setToolTraces] = React.useState<McpToolTrace[]>([]);

  const [useDrift, setUseDrift] = React.useState(
    options.defaultUseDrift ?? true
  );
  const [useMcp, setUseMcp] = React.useState(options.defaultUseMcp ?? true);
  const [forceMcpRefresh, setForceMcpRefresh] = React.useState(false);

  const [mcpServerIds, setMcpServerIds] = React.useState<ID[]>([]);
  const [mcpCapabilityNames, setMcpCapabilityNames] = React.useState<string[]>(
    []
  );

  const { data: pipelineResp } = useFetchDriftPipeline();
  const pipelineId = React.useMemo(
    () => resolvePipelineId(pipelineResp),
    [pipelineResp]
  );

  const {
    data: historyResp,
    refetch: refetchHistory,
    isFetching: historyLoading,
  } = useFetchChatSessionMessages(selectedSessionId, {
    enabled: !!selectedSessionId,
  });

  const { send, cancel, sending, streamStatus } = useChatStream();

  React.useEffect(() => {
    const serverMessages = historyResp?.messages || [];

    setMessages(
      serverMessages.filter(
        (message) =>
          message.role === 'user' || message.role === 'assistant'
      )
    );

    setToolTraces([]);
  }, [historyResp?.messages, selectedSessionId]);

  const setSelectedSessionId = React.useCallback((sid: string | null) => {
    setSelectedSessionIdState(sid);
    setMessages([]);
    setToolTraces([]);
  }, []);

  const createNewChat = React.useCallback(() => {
    const sid = newSessionId();

    upsertSession(sid, 'New chat');
    setSelectedSessionIdState(sid);
    setMessages([]);
    setToolTraces([]);
    setInput('');
  }, [upsertSession]);

  const removeChat = React.useCallback(
    (sid: string) => {
      deleteSession(sid);

      if (selectedSessionId === sid) {
        setSelectedSessionIdState(null);
        setMessages([]);
        setToolTraces([]);
        setInput('');
      }
    },
    [deleteSession, selectedSessionId]
  );

  const appendAssistantToken = React.useCallback((token: string) => {
    setMessages((prev) => {
      const last = prev[prev.length - 1];

      if (last?.role === 'assistant' && !last.id) {
        return [
          ...prev.slice(0, -1),
          {
            ...last,
            content: `${last.content}${token}`,
          },
        ];
      }

      return [
        ...prev,
        {
          role: 'assistant',
          content: token,
          created_at: new Date().toISOString(),
        },
      ];
    });
  }, []);

  const sendMessage = React.useCallback(async () => {
    const userText = input.trim();

    if (!userText || sending) return;

    const sid = selectedSessionId || newSessionId();

    if (!selectedSessionId) {
      upsertSession(sid, 'New chat');
      setSelectedSessionIdState(sid);
    }

    setInput('');

    await send({
      payload: {
        session_id: sid,
        message: userText,
        retrieval: {
          drift: useDrift,
          pipeline_id: pipelineId || undefined,
        },
        mcp: {
          enabled: useMcp,
          server_ids: mcpServerIds,
          capability_names: mcpCapabilityNames,
          force_refresh: forceMcpRefresh,
        },
        related_type: options.related_type,
        related_id: options.related_id,
      },
      onUserMessage: (message) => {
        setMessages((prev) => [...prev, message]);
      },
      onAssistantToken: appendAssistantToken,
      onAssistantMessage: (message) => {
        setMessages((prev) => {
          const last = prev[prev.length - 1];

          const withoutStreamingPlaceholder =
            last?.role === 'assistant' && !last.id
              ? prev.slice(0, -1)
              : prev;

          return [...withoutStreamingPlaceholder, message];
        });
      },
      onToolTrace: (trace) => {
        setToolTraces((prev) => [...prev, trace]);
      },
      onDone: () => {
        upsertSession(sid, userText.slice(0, 80));
        refetchHistory();
      },
    });
  }, [
    appendAssistantToken,
    forceMcpRefresh,
    input,
    mcpCapabilityNames,
    mcpServerIds,
    options.related_id,
    options.related_type,
    pipelineId,
    refetchHistory,
    selectedSessionId,
    send,
    sending,
    upsertSession,
    useDrift,
    useMcp,
  ]);

  return {
    sessions,
    selectedSessionId,
    setSelectedSessionId,
    createNewChat,
    removeChat,

    input,
    setInput,
    messages,
    historyLoading,

    sendMessage,
    cancel,
    sending,
    streamStatus,

    useDrift,
    setUseDrift,
    pipelineId,

    useMcp,
    setUseMcp,
    forceMcpRefresh,
    setForceMcpRefresh,

    mcpServerIds,
    setMcpServerIds,
    mcpCapabilityNames,
    setMcpCapabilityNames,
    toolTraces,
  };
}